import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Users, Target, Eye, ShieldCheck, Award, Building2,
  MapPin, Calendar, ChevronRight
} from "lucide-react";

const directors = [
  {
    name: "Thilak Kumar H S",
    role: "Director",
    desc: "Visionary leader driving Assistive Netspeed's growth across ISP and telecom sectors with over a decade of industry expertise.",
  },
  {
    name: "Sujatha M",
    role: "Director",
    desc: "Instrumental in shaping the company's operational excellence and brand expansion strategy across India.",
  },
];

const certifications = [
  { name: "DOT ISP License", desc: "Licensed Internet Service Provider by Department of Telecommunications, India" },
  { name: "TRAI Registered MSO", desc: "Registered Multi-System Operator for IPTV operations across India" },
  { name: "IP-1 License", desc: "Infrastructure Provider Category-I license for fiber leasing operations" },
  { name: "ISO 9001:2015", desc: "Quality Management System certified for telecom service delivery" },
];

const milestones = [
  { year: "Founded", event: "Assistive Netspeed Technologies incorporated in Bengaluru" },
  { year: "ISP Launch", event: "Launched broadband services in Karnataka" },
  { year: "IPTV", event: "Became a licensed MSO for IPTV operations pan-India" },
  { year: "1M Users", event: "Crossed nearly one million broadband subscribers" },
  { year: "3 Brands", event: "Expanded to ANT Broadband, A Digital, and ANT TV" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section
        data-testid="about-hero"
        className="relative py-24 md:py-32 bg-[#0A192F] overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#EA580C] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#F97316] rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            About Us
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white font-['Outfit'] leading-[1.1] mb-6">
            Powering India's
            <br />
            <span className="text-[#F97316]">Telecom Backbone</span>
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
            A Bengaluru-based telecom conglomerate with national operations, serving a million users
            through broadband, IPTV, and enterprise connectivity solutions.
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section data-testid="vision-mission" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <img
                src="https://images.pexels.com/photos/4373997/pexels-photo-4373997.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Fiber optic infrastructure"
                className="w-full h-[450px] object-cover"
              />
            </div>
            <div>
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#0A192F] rounded-sm flex items-center justify-center">
                    <Target className="w-5 h-5 text-[#EA580C]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0A192F] font-['Outfit']">Our Mission</h3>
                </div>
                <p className="text-base leading-relaxed text-[#334155]">
                  To democratize digital connectivity across India by delivering affordable, reliable,
                  and high-speed internet and entertainment services to every household and business,
                  while maintaining the highest standards of service quality and customer satisfaction.
                </p>
              </div>
              <Separator className="my-8 bg-[#E2E8F0]" />
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#0A192F] rounded-sm flex items-center justify-center">
                    <Eye className="w-5 h-5 text-[#EA580C]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0A192F] font-['Outfit']">Our Vision</h3>
                </div>
                <p className="text-base leading-relaxed text-[#334155]">
                  To be India's most trusted telecom infrastructure and digital services company,
                  recognized for innovation, operational excellence, and unwavering commitment to
                  connecting every corner of the nation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Directors */}
      <section data-testid="directors-section" className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            Leadership
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-12">
            Board of Directors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {directors.map((dir) => (
              <Card
                key={dir.name}
                data-testid={`director-${dir.name.toLowerCase().replace(/\s/g, "-")}`}
                className="p-8 bg-white border border-[#E2E8F0] shadow-sm rounded-none"
              >
                <div className="flex items-start gap-5">
                  <div className="w-16 h-16 bg-[#0A192F] rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl font-bold text-[#EA580C] font-['Outfit']">
                      {dir.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#0A192F] font-['Outfit'] mb-1">
                      {dir.name}
                    </h3>
                    <Badge className="bg-[#EA580C] text-white text-[10px] uppercase tracking-wider mb-3">
                      {dir.role}
                    </Badge>
                    <p className="text-sm text-[#64748B] leading-relaxed">{dir.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section data-testid="timeline-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            Our Journey
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-12">
            Key Milestones
          </h2>
          <div className="space-y-0">
            {milestones.map((m, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 bg-[#0A192F] rounded-sm flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-4 h-4 text-[#EA580C]" />
                  </div>
                  {i < milestones.length - 1 && (
                    <div className="w-px h-16 bg-[#E2E8F0]" />
                  )}
                </div>
                <div className="pb-8">
                  <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#EA580C]">
                    {m.year}
                  </span>
                  <p className="text-base text-[#334155] mt-1">{m.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section data-testid="certifications-section" className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
            Trust & Compliance
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-12">
            Certifications & Licenses
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, i) => (
              <Card
                key={cert.name}
                data-testid={`cert-card-${i}`}
                className="p-6 bg-white border border-[#E2E8F0] shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 rounded-none"
              >
                <ShieldCheck className="w-8 h-8 text-[#EA580C] mb-4" />
                <h4 className="text-base font-semibold text-[#0A192F] font-['Outfit'] mb-2">
                  {cert.name}
                </h4>
                <p className="text-sm text-[#64748B] leading-relaxed">{cert.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Office Info */}
      <section data-testid="office-section" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#EA580C] mb-4 block">
                Our Office
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0A192F] font-['Outfit'] mb-6">
                Headquartered in Bengaluru
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex gap-3 items-start">
                  <MapPin className="w-5 h-5 text-[#EA580C] mt-0.5 flex-shrink-0" />
                  <p className="text-base text-[#334155]">
                    #330, Whitecity Layout, Opp to Blossom International School, Bengaluru-560049
                  </p>
                </div>
                <div className="flex gap-3 items-center">
                  <Users className="w-5 h-5 text-[#EA580C] flex-shrink-0" />
                  <p className="text-base text-[#334155]">100+ dedicated employees</p>
                </div>
                <div className="flex gap-3 items-center">
                  <Building2 className="w-5 h-5 text-[#EA580C] flex-shrink-0" />
                  <p className="text-base text-[#334155]">Operations across India</p>
                </div>
              </div>
              <Link to="/contact">
                <Button
                  data-testid="about-contact-btn"
                  className="bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-sm px-6"
                >
                  Visit Us <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div>
              <img
                src="https://images.pexels.com/photos/4508751/pexels-photo-4508751.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Data center infrastructure"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
